// hooks/useQueryParams.ts
import { ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { LocationQueryValue, LocationQueryValueRaw } from 'vue-router'

// Базовый тип для значений
type BaseQueryValue = string | number | boolean | null | undefined

// Расширенный тип с индексной сигнатурой
type QueryValue = BaseQueryValue | BaseQueryValue[] | Record<string, BaseQueryValue>

// Тип с индексной сигнатурой
type QueryParams = {
  [key: string]: QueryValue
}

export function useQueryParams<T extends QueryParams = QueryParams>() {
  const route = useRoute()
  const router = useRouter()

  // Парсинг значения
  const parseValue = (value: LocationQueryValue | LocationQueryValue[]): QueryValue => {
    if (value === null || value === undefined) {
      return undefined
    }

    // Обработка массива значений
    if (Array.isArray(value)) {
      if (value.length === 0) return undefined

      const parsed = value.map((item) => {
        if (item === null || item === undefined) return item
        try {
          return JSON.parse(item)
        } catch {
          return item
        }
      })

      // Если все элементы одного типа
      if (parsed.length > 0) {
        const firstType = typeof parsed[0]
        if (parsed.every((item) => typeof item === firstType)) {
          return parsed
        }
      }

      return parsed.length === 1 ? parsed[0] : parsed
    }

    // Одиночное значение
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }

  // Парсинг всех параметров
  const parseQueryParams = (): Partial<T> => {
    const params: Partial<T> = {}

    for (const [key, value] of Object.entries(route.query)) {
      const parsedValue = parseValue(value)
      if (parsedValue !== undefined) {
        // Используем type assertion для обхода проверки типов
        ;(params as any)[key] = parsedValue
      }
    }

    return params
  }

  // Сериализация значения
  const stringifyValue = (value: QueryValue): LocationQueryValueRaw => {
    if (value === null || value === undefined || value === '') {
      return undefined
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item === null || item === undefined) return 'null'
          if (typeof item === 'object') {
            return JSON.stringify(item)
          }
          return String(item)
        })
        .join('') // <-- Проверить здесь
    }

    if (typeof value === 'object') {
      return JSON.stringify(value)
    }

    return String(value)
  }

  const queryParams = ref<Partial<T>>(parseQueryParams())

  // Обновление параметров
  const updateQueryParams = (params?: Partial<T>) => {
    const query: Record<string, LocationQueryValueRaw> = {}

    for (const [key, value] of Object.entries(params || [])) {
      query[key] = stringifyValue(value as QueryValue)
    }

    router.replace({ query })
  }

  // Обновление одного параметра
  const updateParam = <K extends keyof T>(key: K, value: T[K]) => {
    const newParams = { ...queryParams.value }
    // Используем any для обхода проверки типов
    ;(newParams as any)[key] = value
    updateQueryParams(newParams)
  }

  watch(
    () => route.query,
    () => {
      queryParams.value = parseQueryParams()
    },
    { deep: true },
  )

  const params = computed({
    get: () => queryParams.value,
    set: (value: Partial<T>) => updateQueryParams(value),
  })

  return {
    params,
    queryParams,
    updateQueryParams,
    updateParam,
    clearQueryParams: () => router.replace({ query: {} }),
    hasParam: (key: keyof T) => key in queryParams.value,
    getParam: <K extends keyof T>(key: K): T[K] | undefined => queryParams.value[key],
    parseQueryParams,
  }
}
